import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import React, {useContext, useEffect, useState} from 'react';
import {CopyAll, Update} from '@mui/icons-material';
import APIWrapperContext from '../../../../../services/APIWrapperContext';
import {ArbeitszeitService, BauvorhabenWithID} from '../../../../../services/openapi';
import {useTranslation} from 'react-i18next';
/**
 * LinkGeneratorForNFC - A component that generates NFC links for time tracking
 *
 * This component allows users to select a construction project (Bauvorhaben)
 * and generate a link that can be used with NFC tags for time tracking.
 */
const LinkGeneratorForNFC: React.FC = () => {
  // Context for API calls
  const {call_backend_api} = useContext(APIWrapperContext);
  const {t} = useTranslation();
  // State for the list of construction projects
  const [bauvorhabenList, setBauvorhabenList] = useState<BauvorhabenWithID[]>([]);

  // State for the generated URL
  const [urlLink, setUrlLink] = useState<string>('');

  // State for the selected construction project UUID
  const [selectedBauvorhabenByUuid, setSelectedBauvorhabenByUuid] = useState<string>('');

  // Environment-specific base URL
  const getBaseUrl = (): string => {
    return import.meta.env.DEV
      ? 'http://localhost:3000'
      : import.meta.env.VITE_FRONTEND_URL || '';
  };

  /**
   * Fetches the list of construction projects from the API
   */
  const fetchBauvorhaben = (): void => {
    call_backend_api(
      ArbeitszeitService.getAlleBauvorhabenArbeitzeitBauvorhabenGet(),
      (res: BauvorhabenWithID[]) => {
        setBauvorhabenList(res);
      },
      undefined,
      undefined,
      false
    );
  };

  // Load construction projects on component mount
  useEffect(() => {
    fetchBauvorhaben();
  }, []);

  /**
   * Handles the selection of a construction project
   * @param event The select change event
   */
  const handleChange = (event: SelectChangeEvent): void => {
    setUrlLink('');
    setSelectedBauvorhabenByUuid(event.target.value);
  };

  /**
   * Generates the NFC link for the selected construction project
   */
  const handleGenerateNFC = (): void => {
    if (!selectedBauvorhabenByUuid) return;

    const selectedBauvorhaben = bauvorhabenList.find(
      (el) => el.bvh_uuid === selectedBauvorhabenByUuid
    );

    if (selectedBauvorhaben?.bvh_uuid) {
      const baseUrl = getBaseUrl();
      setUrlLink(`${baseUrl}/zeiterfassung/stempeln/${selectedBauvorhaben.bvh_uuid}`);
    }
  };

  /**
   * Copies the generated link to the clipboard
   */
  const handleCopyToClipboard = (): void => {
    if (urlLink) {
      navigator.clipboard
        .writeText(urlLink)
        .then(() => {
          // Could add success notification here
          console.log('Link copied to clipboard');
        })
        .catch((err) => {
          console.error('Failed to copy link: ', err);
        });
    }
  };

  return (
    <Card sx={{display: 'flex', flexDirection: 'column'}}>
      <CardHeader title={<Typography variant='h4'>{`${t('generate_nfc_link')}`}</Typography>} />

      <CardContent sx={{display: 'flex', flexDirection: {xs: 'column', lg: 'row'}}}>
        <Box sx={{display: 'flex', flexDirection: 'row'}}>
          <Box sx={{display: 'flex', flexDirection: 'column'}}>
            <Box sx={{flex: 1}} />
            <IconButton onClick={fetchBauvorhaben} aria-label='Refresh list'>
              <Update />
            </IconButton>
            <Box sx={{flex: 1}} />
          </Box>

          <FormControl variant='standard' sx={{m: 1, minWidth: 250}}>
            <InputLabel id='bauvorhaben-select-label'>{`${t('project')}`}</InputLabel>
            <Select
              labelId='bauvorhaben-select-label'
              id='bauvorhaben-select'
              value={selectedBauvorhabenByUuid}
              onChange={handleChange}
              label='Bauvorhaben'>
              <MenuItem value=''>
                <em>None</em>
              </MenuItem>
              {bauvorhabenList.map((bauvorhaben) => (
                <MenuItem key={bauvorhaben.bvh_uuid} value={bauvorhaben.bvh_uuid}>
                  {bauvorhaben.bvh_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Button
          sx={{m: 1}}
          variant='contained'
          onClick={handleGenerateNFC}
          disabled={!selectedBauvorhabenByUuid}>
          {t('generate_nfc_link')}
        </Button>

        {urlLink && (
          <Box sx={{flex: 1, display: 'flex', flexDirection: 'row', m: 1}}>
            <TextField
              sx={{flex: 1}}
              id='nfc-link-field'
              label='NFC Link'
              variant='outlined'
              value={urlLink}
              InputProps={{
                readOnly: true,
              }}
            />
            <IconButton
              aria-label='copy to clipboard'
              onClick={handleCopyToClipboard}
              title='Copy to clipboard'>
              <CopyAll />
            </IconButton>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default LinkGeneratorForNFC;
